import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, useScroll } from 'framer-motion';
import { ArrowRight, Radio } from 'lucide-react';
import { EASE } from '../lib/motion';
import { scrollTo } from '../lib/scroll';

const LINES = [
  { text: 'Werbung', accent: false },
  { text: 'Wahrnehmung', accent: false },
  { text: 'Wirkung', accent: true },
];

const METRICS = [
  { value: '50', label: 'Bildschirme an den frequenzstärksten Punkten' },
  { value: '1,8 Mio.', label: 'Einkäufe pro Jahr an unseren Kassen' },
  { value: '48.000', label: 'Menschen pro Woche im Markt' },
  { value: '40 km', label: 'Einzugsgebiet rund um Koblenz' },
];

const SLIDES = [
  { kind: 'partner', tag: 'Partner-Spot · 10 Sek.', title: 'Hier läuft Ihre Botschaft', big: '260\u202F×', sub: 'am Tag auf jedem Bildschirm' },
  { kind: 'markt', tag: 'Markt · Frische', title: 'Erdbeeren aus der Region', big: '2,49 €', sub: 'die Schale · nur diese Woche' },
  { kind: 'partner', tag: 'Partner-Spot · 10 Sek.', title: 'Ihr Angebot. Ihr Preis.', big: 'Sichtbar', sub: 'mit dem Einkaufswagen in der Hand' },
  { kind: 'markt', tag: 'Markt · Rezept der Woche', title: 'Flammkuchen mit Ziegenkäse', big: '20 Min.', sub: 'alle Zutaten an Theke 4' },
];

function ScreenMockup() {
  const ref = useRef(null);
  const [i, setI] = useState(0);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 140, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 140, damping: 18 });
  const { scrollY } = useScroll();
  const py = useTransform(scrollY, [0, 700], [0, 90]);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % SLIDES.length), 3200);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  const s = SLIDES[i];
  const partner = s.kind === 'partner';

  return (
    <motion.div style={{ y: py }} className="relative">
      <div className="pointer-events-none absolute -inset-10 rounded-full bg-[#FF6B00]/15 blur-3xl" />
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 0.5 }}
        data-testid="hero-spot-mockup"
        className="relative rounded-[22px] border border-[#282C37] bg-[#16181D] p-2 shadow-2xl shadow-black/60"
      >
        <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-[#0F1012]">
          <div className="absolute inset-x-0 top-0 flex items-center justify-between px-6 pt-5">
            <span className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#7D8391]">{s.tag}</span>
            <span className="flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-widest text-[#FF6B00]">
              <Radio className="h-3 w-3 animate-spot-pulse" /> Live
            </span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="absolute inset-x-0 bottom-16 top-12 flex flex-col items-center justify-center px-8 text-center"
            >
              {partner && (
                <div className="flex h-9 w-9 items-center justify-center rounded-md border-2 border-dashed border-[#282C37] font-mono-num text-[8px] uppercase tracking-widest text-[#7D8391]">Logo</div>
              )}
              <p className={`mt-2.5 font-display font-extrabold leading-tight tracking-tight ${partner ? 'text-lg sm:text-xl' : 'text-base text-[#B6BCC8] sm:text-lg'}`}>{s.title}</p>
              <p className={`mt-1 font-mono-num text-2xl font-bold leading-none sm:text-3xl ${partner ? 'text-[#FF6B00]' : 'text-white'}`}>{s.big}</p>
              <p className="mt-1.5 text-xs text-[#B6BCC8]">{s.sub}</p>
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-x-0 bottom-0 px-6 pb-5">
            <div className="flex gap-1">
              {SLIDES.map((sl, k) => (
                <span key={`${sl.kind}-${sl.title}`} className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${k === i ? 'bg-[#FF6B00]' : 'bg-[#282C37]'}`} />
              ))}
            </div>
            <p className="mt-3 text-center font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#7D8391]">Kassen · Mall · Theken · Leergut · Eingang</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  return (
    <section id="start" data-testid="hero-section" className="grain relative overflow-hidden pt-32 sm:pt-40">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '28px 28px' }}
      />
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full bg-[#FF6B00]/10 blur-[120px]" />

      <div className="wrap relative pb-20 sm:pb-28">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="eyebrow"
              data-testid="hero-eyebrow"
            >
              Instore Media · GLOBUS Markthalle Koblenz
            </motion.p>
            <h1 className="mt-6 font-display text-5xl font-black leading-none tracking-tight sm:text-6xl lg:text-[5.5rem]" data-testid="hero-headline">
              {LINES.map((l, k) => (
                <span key={l.text} className="block overflow-hidden pb-[0.06em]">
                  <motion.span
                    className={`block ${l.accent ? 'text-[#FF6B00]' : ''}`}
                    initial={{ y: '110%' }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.15 + k * 0.18 }}
                  >
                    {l.text}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
              className="lead mt-8 max-w-[56ch]"
              data-testid="hero-subline"
            >
              50 Bildschirme an den meistbesuchten Punkten der GLOBUS Markthalle Koblenz – an den Kassen, in der Mall, an den
              Bedientheken und in den Wartezonen. Ihre Botschaft läuft dort, wo die Kaufentscheidung fällt.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.75 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a href="#pakete" onClick={(e) => { e.preventDefault(); scrollTo('#pakete'); }} data-testid="hero-cta-order" className="btn-primary">
                Pakete ansehen <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#zonen" onClick={(e) => { e.preventDefault(); scrollTo('#zonen'); }} data-testid="hero-cta-zones" className="btn-ghost">
                Die fünf Zonen
              </a>
              <span className="t-muted ml-1 hidden sm:inline">ab 390 € netto pro Monat</span>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <ScreenMockup />
          </div>
        </div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.1, delayChildren: 0.9 } } }}
          className="mt-24 grid grid-cols-2 gap-x-8 gap-y-10 border-t border-[#282C37] pt-10 lg:grid-cols-4"
          data-testid="hero-metrics"
        >
          {METRICS.map((m, i) => (
            <motion.div key={m.label} variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } }} data-testid={`hero-metric-${i}`}>
              <p className="font-mono-num text-4xl font-bold tracking-tighter text-white sm:text-5xl">{m.value}</p>
              <p className="t-muted mt-3 max-w-[22ch] !text-[#B6BCC8]">{m.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
